import React from 'react';
import { RADIUS, TYPE, useTheme } from '../../theme';

type CardProps = React.HTMLAttributes<HTMLDivElement> & {
  /** Inre marginal i px (0 för kort med bild i kanten). */
  padding?: number;
  /** Lyft kortet med skugga i stället för bara kant. */
  raised?: boolean;
};

/** Yta med kant och radie 16. */
export function Card({ padding = 18, raised, style, children, ...rest }: CardProps) {
  const T = useTheme();
  return (
    <div {...rest} style={{
      background: T.surface, border: `1px solid ${T.border}`, borderRadius: RADIUS.lg,
      padding, boxShadow: raised ? T.shadow.raised : undefined, overflow: padding === 0 ? 'hidden' : undefined, ...style,
    }}>
      {children}
    </div>
  );
}

/** Rubrikrad i ett kort: titel, valfri undertext och en åtgärd till höger. */
export function CardHeader({ title, sub, action }: { title: React.ReactNode; sub?: React.ReactNode; action?: React.ReactNode }) {
  const T = useTheme();
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ ...TYPE.title, fontSize: 16, color: T.text }}>{title}</div>
        {sub && <div style={{ ...TYPE.small, color: T.textMuted, marginTop: 2 }}>{sub}</div>}
      </div>
      {action}
    </div>
  );
}

/** Sektionsrubrik 22/800 med valfri länk eller knapp till höger. */
export function SectionHeader({ title, kicker, action, as: Tag = 'h2', style }: {
  title: React.ReactNode; kicker?: React.ReactNode; action?: React.ReactNode; as?: 'h1' | 'h2' | 'h3'; style?: React.CSSProperties;
}) {
  const T = useTheme();
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, margin: '0 0 14px', ...style }}>
      <div style={{ minWidth: 0 }}>
        {kicker && <div style={{ ...TYPE.small, color: T.textMuted, marginBottom: 2 }}>{kicker}</div>}
        <Tag style={{ ...TYPE.section, color: T.text, margin: 0 }}>{title}</Tag>
      </div>
      {action && <div style={{ flexShrink: 0 }}>{action}</div>}
    </div>
  );
}

/** Liten rubrik i versaler (12/700) för grupper i listor och menyer. */
export function Label({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  const T = useTheme();
  return <div style={{ ...TYPE.label, color: T.textMuted, ...style }}>{children}</div>;
}
